import { useEffect, useRef } from 'react';

const VALUES = [
  {
    icon: 'ri-shield-check-line',
    title: 'Security First',
    desc: 'Every product we build is designed with user privacy and data security as the primary concern.',
  },
  {
    icon: 'ri-code-s-slash-line',
    title: 'Native iOS Excellence',
    desc: 'We build exclusively for the iOS platform, allowing us to craft deeply optimized native experiences.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'User-Centered Design',
    desc: 'Our interfaces are intuitive and refined, ensuring a seamless experience for every user.',
  },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = [contentRef.current, imageRef.current];
            els.forEach((el, i) => {
              if (!el) return;
              el.style.transition = `opacity 0.9s ease ${i * 0.2}s, transform 0.9s ease ${i * 0.2}s`;
              el.style.opacity = '1';
              el.style.transform = 'translateY(0)';
            });
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (contentRef.current) {
      contentRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'translateY(40px)';
    }
    if (imageRef.current) {
      imageRef.current.style.opacity = '0';
      imageRef.current.style.transform = 'translateY(40px)';
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-5">
          <span className="w-8 h-px bg-brand-DEFAULT" />
          <span className="text-brand-DEFAULT text-xs font-medium tracking-[0.25em] uppercase">About Us</span>
        </div>

        <div className="grid grid-cols-2 gap-20 items-start">
          {/* Left: Text content */}
          <div ref={contentRef}>
            <h2 className="font-serif text-brand-dark mb-8" style={{ fontSize: 'clamp(32px, 3.5vw, 52px)', lineHeight: '1.15', fontWeight: 700 }}>
              A Team Dedicated<br />
              to <em className="not-italic text-brand-DEFAULT">iOS</em> Innovation
            </h2>

            <p className="text-gray-600 text-base leading-relaxed mb-6">
              VALDESTA OOD is a focused team of developers and designers with deep
              expertise in the Apple ecosystem. We bring together engineering
              precision and thoughtful design to build applications that truly
              serve the people who use them.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-12">
              Our work centers on two key challenges modern mobile users face:
              maintaining their privacy online and keeping their devices running
              at peak performance. Every product we release reflects our
              commitment to craftsmanship and reliability.
            </p>

            {/* Values */}
            <div className="flex flex-col gap-6">
              {VALUES.map((v) => (
                <div key={v.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-pale text-brand-DEFAULT flex-shrink-0">
                    <i className={`${v.icon} text-lg`} />
                  </div>
                  <div>
                    <h4 className="font-sans font-semibold text-brand-dark text-sm mb-1">{v.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Image stack */}
          <div ref={imageRef} className="relative">
            {/* Main image */}
            <div className="w-full h-96 rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/975db7bac3a562b6f8969e89f605c347.jpg"
                alt="VALDESTA OOD development team"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Second smaller image */}
            <div className="absolute -bottom-8 -left-8 w-52 h-36 rounded-xl overflow-hidden border-4 border-white">
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20iPhone%20screen%20displaying%20clean%20modern%20iOS%20app%20interface%20with%20elegant%20dark%20green%20ui%20elements%20on%20a%20polished%20desk%20surface%20professional%20product%20photography%20white%20and%20neutral%20background%20minimal%20composition%20high%20detail%20crisp%20screen&width=350&height=240&seq=about-iphone-valdesta-1&orientation=landscape"
                alt="iOS app interface on iPhone"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Stat badge */}
            <div className="absolute -top-5 -right-5 bg-brand-DEFAULT text-white rounded-2xl px-6 py-4">
              <p className="font-serif text-3xl font-bold leading-none">iOS</p>
              <p className="text-brand-muted text-xs tracking-widest uppercase mt-1">Platform Only</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
